/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch } from 'redux';
import _ from 'lodash';

import * as Service from 'types/service';
import { PartyUpdate } from 'state/service/appTs/types';
import { RootElmActionReturnType, sendLocalAction } from 'actions/service/rootElmAction';

import { ChronoUnit, Instant, LocalDateTime, ZoneOffset } from '@js-joda/core';
import {
  EventAction,
  EventAttributesActionLocation,
  EventCustomAttributes,
} from 'utils/analytics/amplitudeEventConstants';
import { RealtimePartyDetails } from 'middleware/realtimePos';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { getNameForParty, isPartyQuotedWalkIn } from 'utils/partyUtils';
import { updateTableStates as updatePosTableStates } from 'actions/PosActions';

export interface FunctionTypes {
  addMultipleExtraTablesToParty(
    party: Service.Party,
    tables: Service.ServiceTable[]
  ): BatchCallAction;
  addExtraTableToParty(partyId: number, tableId: number): RootElmActionReturnType;
  addNeedToParty(party: Service.Party, need: any): RootElmActionReturnType;
  addWalkInParty(walkInParty: any): RootElmActionReturnType;
  markTextMessagesAsRead(partyId: number): RootElmActionReturnType;
  removePartyFromTable(partyId: number, tableId: number): RootElmActionReturnType;
  removeNeedFromParty(party: Service.Party, need: any): RootElmActionReturnType;
  removeWalkInParty(party: Service.Party): RootElmActionReturnType;
  replaceTablesForParty(partyId: number, tableId: number): RootElmActionReturnType;
  seatWalkInParty(walkInPartyDescription: any, tables: Service.ServiceTable[]): BatchCallAction;
  setShortNotes(party: Service.Party, note: string): RootElmActionReturnType;
  updatePartyFirstName(party: Service.Party, newFirstName: string): RootElmActionReturnType;
  updatePartyLastName(party: Service.Party, newLastName: string): RootElmActionReturnType;
  updatePartyPhone(party: Service.Party, newPhone: string): RootElmActionReturnType;
  updatePartyQuotedTimeMinutes(
    party: Service.Party,
    newQuotedTimeMinutes: string
  ): RootElmActionReturnType;
  updatePartyReservationTime(
    party: Service.Party,
    newReservationTime: string
  ): RootElmActionReturnType;
  updatePartySize(party: Service.Party, newSize: string): RootElmActionReturnType;
  sendChatMessage(text: any): RootElmActionReturnType;
  updatePartyState(
    party: Service.Party,
    newState: string,
    secondaryState: string | null,
    table?: Service.ServiceTable
  ): BatchCallAction;
}

export function addMultipleExtraTablesToParty(
  party: Service.Party,
  tables: Service.ServiceTable[]
) {
  return (dispatch: Dispatch<any>) => {
    const partyId = party.id;
    const tableIds = _.map(tables, 'id');
    dispatch(sendLocalAction('addMultipleExtraTablesToParty', { partyId, tableIds }));
    if (party.state === 'SEATED') {
      dispatch(updatePosTableStates(party, 'SEATED', tables));
    }
  };
}

export function addMultipleTableIdsToParty(partyId: number, tableIds: number[]) {
  return sendLocalAction('addMultipleExtraTablesToParty', { partyId, tableIds });
}

export function addExtraTableToParty(
  partyId: number,
  tableId: number,
  originalPurchaseId: number | undefined
) {
  amplitudeSendEvent(EventAction.BOOKING_ASSIGN_TABLE, { purchaseId: originalPurchaseId });
  return sendLocalAction(
    'addExtraTableToParty',
    { partyId, tableId },
    { trackAsPostSelectionAction: true }
  );
}

export function addNeedToParty(party: Service.Party, need: any) {
  const partyUpdate: PartyUpdate = { partyId: party.id, updateType: 'AddTag', newValue: need };
  return sendLocalAction('updateParty', partyUpdate, {
    keyToTrack: 'updateType',
    trackAsPostSelectionAction: true,
  });
}

export function addWalkInParty(walkInParty: any) {
  return sendLocalAction('addWalkInParty', walkInParty);
}

export function markTextMessagesAsRead(partyId: number) {
  return sendLocalAction('markTextMessagesAsRead', { partyId });
}

export function removePartyFromTable(
  partyId: number,
  tableId: number,
  originalPurchaseId: number | undefined
) {
  amplitudeSendEvent(EventAction.BOOKING_REMOVE_TABLE, { purchaseId: originalPurchaseId });
  return sendLocalAction(
    'removePartyFromTable',
    { partyId, tableId },
    { trackAsPostSelectionAction: true }
  );
}

export function removeNeedFromParty(party: Service.Party, need: any) {
  const partyUpdate: PartyUpdate = { partyId: party.id, updateType: 'RemoveTag', newValue: need };
  return sendLocalAction('updateParty', partyUpdate, {
    keyToTrack: 'updateType',
    trackAsPostSelectionAction: true,
  });
}

export function removeWalkInParty(party: Service.Party) {
  return sendLocalAction('removeWalkInParty', { partyId: party.id });
}

export function seatWalkInParty(
  walkInPartyDescription: Service.ElmWalkInPartyDescription,
  tables: Service.ServiceTable[]
) {
  return (dispatch: Dispatch<any>) => {
    const tableIds = _.map(tables, 'id');
    dispatch(sendLocalAction('seatWalkInParty', { walkInPartyDescription, tableIds }));

    const party: RealtimePartyDetails = {
      id: walkInPartyDescription.id,
      lastNameFirstName: getNameForParty(walkInPartyDescription),
      type: 'WalkIn',
      ..._.pick(walkInPartyDescription, 'email', 'partySize', 'phone'),
      partyType: { partyType: 'WalkInParty' },
    };
    dispatch(updatePosTableStates(party, 'SEATED', tables));
  };
}

export function setShortNotes(party: Service.Party, note: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'SetShortNotes',
    newValue: note,
  };
  return sendLocalAction('updateParty', partyUpdate, {
    keyToTrack: 'updateType',
    trackAsPostSelectionAction: true,
  });
}

export function updatePartyFirstName(party: Service.Party, newFirstName: string) {
  const partyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartyFirstName',
    newValue: newFirstName,
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function updatePartyLastName(party: Service.Party, newLastName: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartyLastName',
    newValue: newLastName,
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function updatePartyPhone(party: Service.Party, newPhone: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartyPhone',
    newValue: newPhone,
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function updatePartyPhoneCountryCode(party: Service.Party, newPhoneCountryCode: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartyPhoneCountryCode',
    newValue: newPhoneCountryCode,
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function updatePartyQuotedTimeMinutes(party: Service.Party, newQuotedTimeMinutes: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartyQuotedTimeMinutes',
    newValue: newQuotedTimeMinutes,
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function updatePartyReservationTime(party: Service.Party, newReservationTime: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartyReservationTime',
    newValue: newReservationTime,
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function updatePartySize(party: Service.Party, newSize: string) {
  const partyUpdate: PartyUpdate = {
    partyId: party.id,
    updateType: 'UpdatePartySize',
    newValue: String(newSize),
  };
  return sendLocalAction('updateParty', partyUpdate, { keyToTrack: 'updateType' });
}

export function sendChatMessage(text: string) {
  return sendLocalAction('sendChatMessage', { text });
}

export function updatePartyState(
  party: Service.Party,
  newState: string,
  secondaryState: string | null,
  tables: Service.ServiceTable[] = [],
  actionLocation?: EventAttributesActionLocation
) {
  return (dispatch: Dispatch<any>) => {
    dispatch(updateElmPartyState(party, newState, secondaryState));
    maybeSendPosUpdates(
      dispatch,
      party,
      newState,
      secondaryState,
      _.isEmpty(tables) ? party.tables : tables
    );

    sendAmplitudeEvents(party, newState, secondaryState, actionLocation);
  };
}

function sendAmplitudeEvents(
  party: Service.Party,
  newState: string,
  newSecondaryState: string | null,
  actionLocation?: EventAttributesActionLocation
) {
  const additionalData: EventCustomAttributes = {
    actionLocation,
    partyType: party.type.toLowerCase() as Lowercase<Service.Party['type']>,
  };

  // We don't send PARTY_SEATED or PARTY_FINISHED if this does not represent
  // a change to party.state. We do send an event if this is a change only to
  // the party secondary state, however.
  if (newState === party.state) {
    if (party.secondaryState !== newSecondaryState) {
      amplitudeSendEvent(EventAction.PARTY_SECONDARY_STATE_CHANGED, {
        partyState: party.state,
        partySecondaryState: newSecondaryState ?? '[none]',
      });
    }
    return;
  }

  if (isSeatedState(newState)) {
    if (!party.seatedTime) {
      if (party.ticketDateTime) {
        additionalData.seatedTimeOffset = LocalDateTime.parse(party.ticketDateTime).until(
          LocalDateTime.now(),
          ChronoUnit.MINUTES
        );
      }

      if (isPartyQuotedWalkIn(party) && party.arrivalTime && party.quotedTimeMinutes) {
        additionalData.quotedWaitTime = party.quotedTimeMinutes.toString();
        additionalData.actualWaitTime = Instant.ofEpochSecond(party.arrivalTime, 0)
          .until(Instant.now(), ChronoUnit.MINUTES)
          .toString();
      }
    }

    amplitudeSendEvent(EventAction.PARTY_SEATED, additionalData);
  } else if (isFinishedState(newState)) {
    if (party.seatedTime && !party.leftTime) {
      additionalData.expectedTurnTime = party.expectedTurnTimeMinutes;
      additionalData.actualTurnTime = LocalDateTime.ofEpochSecond(
        party.seatedTime,
        ZoneOffset.UTC
      ).until(LocalDateTime.now(ZoneOffset.UTC), ChronoUnit.MINUTES);
    }

    amplitudeSendEvent(EventAction.PARTY_FINISHED, additionalData);
  }
}

function changedToOrFromSeatedState(currentState: string, newState: string) {
  return (
    (isSeatedState(currentState) && !isSeatedState(newState)) ||
    (!isSeatedState(currentState) && isSeatedState(newState))
  );
}

function isSeatedState(state: string) {
  return state === 'SEATED';
}

function isFinishedState(state: string) {
  return state === 'LEFT';
}

function maybeSendPosUpdates(
  dispatch: Dispatch<any>,
  party: Service.Party,
  newState: string,
  secondaryState: string | null,
  tables: Service.ServiceTable[]
) {
  if (changedToOrFromSeatedState(party.state, newState)) {
    dispatch(updatePosTableStates(party, newState, tables));
  }
}

function updateElmPartyState(
  party: Service.Party,
  newState: string,
  secondaryState: string | null
) {
  const partyStateUpdate: any = { partyId: party.id, state: newState };
  // We want `secondaryState` to be undefined if it's not set, so only assign it if it exists
  if (secondaryState) {
    partyStateUpdate.secondaryState = secondaryState;
  }
  return sendLocalAction('updatePartyState', partyStateUpdate, {
    trackAsPostSelectionAction: true,
  });
}

export function replaceTablesForParty(
  partyId: number,
  tableId: number,
  originalPurchaseId: number | undefined
) {
  amplitudeSendEvent(EventAction.BOOKING_REPLACE_TABLE, { purchaseId: originalPurchaseId });
  return sendLocalAction('replaceTablesForParty', { partyId, tableId });
}
